import React, { memo, useEffect, useRef } from 'react';
import { useReactFlow } from 'reactflow';
import useFitView from '@components/MainStage/hooks/useFitView';
import { MultiSelectionBadge } from '@components/MainStage/MultiSelectionBadge';
import { useIsChromeHidden } from '@components/MainStage/uiChrome.store';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  ZOOM_TRANSITION_TIME,
} from '@constants/canvas/general';
import useCanvasZoom from '@hooks/canvas/useCanvasZoom';
import {
  abandonViewportAnimation,
  claimViewportAnimation,
  settleViewportAnimation,
} from '@hooks/canvas/viewportAnimation';
import { Typography } from '@mui/material';

import {
  ButtonWrapper,
  CustomControlsStyled,
  FullViewWrapper,
  StyledFullViewIcon,
  StyledMinusIcon,
  StyledPlusIcon,
  StyledZoom,
  ZoomControls,
} from './CustomControls.styled';

const formatZoom = (zoom: number) => {
  return `${(zoom * 100).toFixed(0)}%`;
};

const steps = [0.5, 0.6, 0.8, 1, 1.2, 1.4, 1.6, 1.8, 2];

const getNextZoom = (currentZoom: number, direction: 'in' | 'out') => {
  const currentIndex = steps.findIndex((step) => step >= currentZoom);

  if (direction === 'in') {
    return steps[Math.min(currentIndex + 1, steps.length - 1)];
  }
  return steps[Math.max(currentIndex - 1, 0)];
};

const normalizeZoom = (zoom: number) => {
  return steps.reduce((prev, curr) =>
    Math.abs(curr - zoom) <= Math.abs(prev - zoom) ? curr : prev,
  );
};

export const CustomControls = memo(function CustomControls() {
  const { zoomTo, getZoom } = useReactFlow();
  const handleFitView = useFitView();
  const { zoom } = useCanvasZoom();
  const isChromeHidden = useIsChromeHidden();

  const pendingZoomRef = useRef<number | null>(null);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
    },
    [],
  );

  const stepZoom = (direction: 'in' | 'out') => {
    const base = pendingZoomRef.current ?? normalizeZoom(getZoom());
    const nextZoom = getNextZoom(base, direction);

    if (nextZoom < MIN_ZOOM || nextZoom > MAX_ZOOM) return;

    pendingZoomRef.current = nextZoom;

    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
    }

    pendingTimerRef.current = setTimeout(() => {
      pendingZoomRef.current = null;
    }, ZOOM_TRANSITION_TIME + 50);

    const token = claimViewportAnimation();

    try {
      zoomTo(nextZoom, { duration: ZOOM_TRANSITION_TIME });
      settleViewportAnimation(token);
    } catch (error) {
      abandonViewportAnimation(token);
      throw error;
    }
  };

  return (
    <CustomControlsStyled>
      <MultiSelectionBadge />

      {!isChromeHidden && (
        <>
          <ZoomControls>
            <ButtonWrapper onClick={() => stepZoom('out')}>
              <StyledMinusIcon />
            </ButtonWrapper>
            <StyledZoom>
              <Typography variant="body2" color="grey.800">
                {formatZoom(zoom)}
              </Typography>
            </StyledZoom>
            <ButtonWrapper onClick={() => stepZoom('in')}>
              <StyledPlusIcon />
            </ButtonWrapper>
          </ZoomControls>

          <FullViewWrapper onClick={handleFitView}>
            <StyledFullViewIcon />
          </FullViewWrapper>
        </>
      )}
    </CustomControlsStyled>
  );
});
